import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiEye } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import agentBillsService from "../../../services/agentBills.service";
import DashboardStatsBar from "../../../components/dashboard/DashboardStatsBar";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import { getApiErrorMessage } from "../../../utils/downloadExcel";
import { emitDashboardRefresh } from "../../../utils/dashboardEvents";
import "../../../styles/users.css";
import "../../../styles/confirmation.css";
import "../../../styles/dashboard.css";
import "../../../styles/repoAgentsBills.css";

const MODE_LABELS = {
  CASH: "Cash",
  UPI: "UPI",
  NEFT: "NEFT",
  IMPS: "IMPS",
  RTGS: "RTGS",
  CHEQUE: "Cheque",
  BANK_TRANSFER: "Bank transfer",
};

function money(value) {
  const n = Number(value);
  const amount = Number.isFinite(n) ? n : 0;
  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function todayInputValue() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function newRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `abp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function emptyOverview() {
  return { seizedVehicles: 0, totalPayable: 0, paid: 0, unpaid: 0, extraPaid: 0 };
}

function paymentTypeLabel(type) {
  if (type === "ADVANCE") return "Extra Paid / Advance";
  if (type === "OTHER") return "Other";
  return "Repo";
}

function emptyForm() {
  return {
    agentId: "",
    confirmationId: "",
    paymentType: "PER_REPO",
    amount: "",
    paymentDate: todayInputValue(),
    paymentMode: "UPI",
    reference: "",
    remarks: "",
  };
}

function serialFor(index, page, total, limit) {
  return Math.max(Number(total || 0) - ((page || 1) - 1) * limit - index, 1);
}

export default function RepoAgentsBills() {
  const { auth } = useAuth();
  const canPay = actsAsAgencyAdmin(auth);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [overview, setOverview] = useState(emptyOverview);
  const [agents, setAgents] = useState([]);
  const [paymentModes, setPaymentModes] = useState(Object.keys(MODE_LABELS));
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [formAgent, setFormAgent] = useState(null);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const requestIdRef = useRef(newRequestId());
  const isFirstLoad = useRef(true);

  useEffect(() => {
    setPage(1);
  }, [appliedQuery]);

  const loadAgents = useCallback(async () => {
    if (!auth?.token) return;
    try {
      const res = await agentBillsService.getAgents(auth.token);
      const list = res?.data?.agents || [];
      setAgents(Array.isArray(list) ? list : []);
      if (Array.isArray(res?.data?.paymentModes) && res.data.paymentModes.length) {
        setPaymentModes(res.data.paymentModes);
      }
    } catch {
      setAgents([]);
    }
  }, [auth?.token]);

  const loadSummaries = useCallback(
    async ({ silent = false } = {}) => {
      const showFullLoader = !silent && isFirstLoad.current;
      if (!auth?.token) {
        if (showFullLoader) setLoading(false);
        return;
      }
      try {
        if (showFullLoader) setLoading(true);
        if (!silent) setError("");
        const params = { page, limit: PAGE_LIMIT };
        if (appliedQuery.trim()) params.q = appliedQuery.trim();
        const res = await agentBillsService.getSummaries(auth.token, params);
        setRows(readListItems(res, ["summaries", "items"]));
        setPagination(readPagination(res));
        const nextOverview = res?.data?.overview || res?.overview;
        setOverview(nextOverview && typeof nextOverview === "object" ? nextOverview : emptyOverview());
        if (Array.isArray(res?.data?.paymentModes) && res.data.paymentModes.length) {
          setPaymentModes(res.data.paymentModes);
        }
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || "Failed to load agent bills.");
          setRows([]);
          setOverview(emptyOverview());
        }
      } finally {
        if (showFullLoader) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    },
    [auth?.token, appliedQuery, page]
  );

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    loadSummaries({ silent: !isFirstLoad.current });
  }, [loadSummaries]);

  useEffect(() => {
    let cancelled = false;
    async function loadFormAgent() {
      if (!auth?.token || !form.agentId) {
        setFormAgent(null);
        return;
      }
      try {
        const res = await agentBillsService.getAgent(auth.token, form.agentId);
        if (!cancelled) setFormAgent(res?.data || null);
      } catch {
        if (!cancelled) setFormAgent(null);
      }
    }
    loadFormAgent();
    return () => {
      cancelled = true;
    };
  }, [auth?.token, form.agentId]);

  const selectedVehicle = useMemo(() => {
    if (!formAgent || !form.confirmationId) return null;
    return (
      (formAgent.vehicles || []).find(
        (row) => String(row.confirmationId) === String(form.confirmationId)
      ) || null
    );
  }, [formAgent, form.confirmationId]);

  const outstandingHint = useMemo(() => {
    if (!formAgent) return 0;
    if (form.paymentType === "ALL_REPO") {
      return Number(formAgent.totals?.unpaid || 0);
    }
    return selectedVehicle ? Number(selectedVehicle.unpaid || 0) : 0;
  }, [formAgent, form.paymentType, selectedVehicle]);

  useEffect(() => {
    setForm((prev) => {
      if (prev.paymentType === "ALL_REPO") {
        const unpaid = Number(formAgent?.totals?.unpaid || 0);
        return {
          ...prev,
          confirmationId: "",
          amount: formAgent && unpaid > 0 ? unpaid.toFixed(2) : "",
        };
      }
      if (selectedVehicle) {
        const unpaid = Number(selectedVehicle.unpaid || 0);
        return {
          ...prev,
          amount: unpaid > 0 ? unpaid.toFixed(2) : "",
        };
      }
      if (!prev.confirmationId) {
        return { ...prev, amount: prev.agentId ? prev.amount : "" };
      }
      return prev;
    });
  }, [form.paymentType, form.agentId, form.confirmationId, formAgent, selectedVehicle]);

  const updateField = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "paymentType" && value === "ALL_REPO") {
        next.confirmationId = "";
      }
      return next;
    });
    setFormError("");
    setFormSuccess("");
  };

  const openDetail = async (agentId) => {
    if (!auth?.token) return;
    setDetailLoading(true);
    setError("");
    try {
      const res = await agentBillsService.getAgent(auth.token, agentId);
      setDetail(res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load agent details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!canPay || saving) return;
    setFormError("");
    setFormSuccess("");

    const amount = Number(form.amount);
    if (!form.agentId) {
      setFormError("Select an agent.");
      return;
    }
    if (form.paymentType === "PER_REPO" && !form.confirmationId) {
      setFormError("Per Repo Payment must be linked to a seized vehicle.");
      return;
    }
    if (!(amount > 0)) {
      setFormError("Payment amount must be greater than zero.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        agentId: form.agentId,
        paymentType: form.paymentType,
        amount,
        paymentDate: form.paymentDate,
        paymentMode: form.paymentMode,
        reference: form.reference.trim(),
        remarks: form.remarks.trim(),
        clientRequestId: requestIdRef.current,
      };
      if (form.paymentType === "PER_REPO" && form.confirmationId) {
        payload.confirmationId = form.confirmationId;
      }
      const res = await agentBillsService.createPayment(auth.token, payload);
      if (!res?.success) {
        setFormError(res?.message || "Could not save payment.");
        return;
      }
      setFormSuccess(res.message || "Payment saved.");
      requestIdRef.current = newRequestId();
      setForm((prev) => ({
        ...emptyForm(),
        agentId: prev.agentId,
        paymentType: prev.paymentType,
        paymentMode: prev.paymentMode,
      }));
      await loadSummaries({ silent: true });
      if (form.agentId) {
        const detailRes = await agentBillsService.getAgent(auth.token, form.agentId);
        setFormAgent(detailRes?.data || null);
        if (detail?.agent?._id && String(detail.agent._id) === String(form.agentId)) {
          setDetail(detailRes?.data || null);
        }
      }
      emitDashboardRefresh({ source: "agent-bills" });
    } catch (err) {
      setFormError(await getApiErrorMessage(err, "Could not save payment."));
    } finally {
      setSaving(false);
    }
  };

  const emptyMessage = appliedQuery.trim()
    ? `No agents match “${appliedQuery.trim()}”.`
    : "No agent bills found for seized vehicles or recorded payments.";

  return (
    <div className="page companies-page cf-page rab-page">
      <div className="users-actions">
        <h2>Repo Agents Bills</h2>
      </div>

      <DashboardStatsBar
        className="dashboard-stats-bar--five"
        items={[
          {
            key: "seized",
            label: "Seized Vehicles",
            icon: "🚘",
            value: overview.seizedVehicles || 0,
            desc: "Inventory-confirmed vehicles",
          },
          {
            key: "total",
            label: "Total Payment",
            icon: "💰",
            value: money(overview.totalPayable),
            desc: "Repo + other payable",
          },
          {
            key: "paid",
            label: "Paid",
            icon: "✅",
            value: money(overview.paid),
            desc: "Amount already paid",
          },
          {
            key: "unpaid",
            label: "Unpaid",
            icon: "⏳",
            value: money(overview.unpaid),
            desc: "Remaining balance",
          },
          {
            key: "extra",
            label: "Extra Paid / Advance",
            icon: "➕",
            value: money(overview.extraPaid),
            desc: "Adjusted against future vehicles",
          },
        ]}
      />

      {canPay ? (
        <section className="cf-table-card card rab-pay-card">
          <h3 className="rab-pay-card__title">Add payment</h3>
          <form className="rab-form" onSubmit={handleSave}>
            <label>
              Select agent
              <select
                value={form.agentId}
                onChange={(e) => updateField("agentId", e.target.value)}
              >
                <option value="">Choose agent</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent._id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Payment type
              <select
                value={form.paymentType}
                onChange={(e) => updateField("paymentType", e.target.value)}
              >
                <option value="PER_REPO">Per Repo Payment</option>
                <option value="ALL_REPO">All Repo Payment</option>
              </select>
            </label>
            <label>
              Select seized vehicle
              <select
                value={form.confirmationId}
                onChange={(e) => updateField("confirmationId", e.target.value)}
                disabled={!form.agentId || form.paymentType === "ALL_REPO"}
              >
                <option value="">
                  {form.paymentType === "ALL_REPO"
                    ? "All seized vehicles"
                    : "Choose vehicle"}
                </option>
                {(formAgent?.vehicles || []).map((vehicle) => (
                  <option key={vehicle.confirmationId} value={vehicle.confirmationId}>
                    {(vehicle.vehicleNumber || "Vehicle") +
                      (vehicle.customerName ? ` · ${vehicle.customerName}` : "") +
                      ` · unpaid ${money(vehicle.unpaid)}`}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Amount
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(e) => updateField("amount", e.target.value)}
              />
            </label>
            <label>
              Payment date
              <input
                type="date"
                value={form.paymentDate}
                onChange={(e) => updateField("paymentDate", e.target.value)}
              />
            </label>
            <label>
              Payment mode
              <select
                value={form.paymentMode}
                onChange={(e) => updateField("paymentMode", e.target.value)}
              >
                {paymentModes.map((mode) => (
                  <option key={mode} value={mode}>
                    {MODE_LABELS[mode] || mode}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Reference / Transaction ID
              <input
                value={form.reference}
                onChange={(e) => updateField("reference", e.target.value)}
                placeholder="Optional"
              />
            </label>
            <label className="rab-form__full">
              Remarks
              <textarea
                rows={2}
                value={form.remarks}
                onChange={(e) => updateField("remarks", e.target.value)}
                placeholder="Optional"
              />
            </label>
            {formAgent ? (
              <p className="rab-hint rab-form__full">
                {outstandingHint > 0
                  ? `Autofilled unpaid: ${money(outstandingHint)}. You can edit this amount before saving.`
                  : "No unpaid balance. This payment will be saved as Extra Paid / Advance."}{" "}
                Agent unpaid: {money(formAgent.totals?.unpaid)}. Extra paid:{" "}
                {money(formAgent.totals?.extraPaid)}.
              </p>
            ) : null}
            {formError ? <p className="error-text rab-form__full">{formError}</p> : null}
            {formSuccess ? <p className="rab-success rab-form__full">{formSuccess}</p> : null}
            <div className="rab-form__full">
              <button type="submit" className="primary-page-btn" disabled={saving}>
                {saving ? "Saving…" : "Save payment"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <form
        className="cf-list-search"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setAppliedQuery(searchQuery.trim());
        }}
      >
        <label className="cf-list-search__field">
          <span className="sr-only">Search by agent name</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by agent name"
            autoComplete="off"
          />
        </label>
        <button type="submit" className="primary-page-btn cf-list-search__btn">
          Search
        </button>
        <button
          type="button"
          className="secondary-page-btn cf-list-search__btn"
          onClick={() => {
            setSearchQuery("");
            setAppliedQuery("");
            setPage(1);
          }}
        >
          Reset
        </button>
      </form>

      {error ? <p className="error-text">{error}</p> : null}

      {loading ? (
        <p className="cf-excel-loading">Loading agent bills…</p>
      ) : (
        <div className="company-table-wrap cf-excel-sheet table-scroll printable-table-area">
          <table className="users-table excel-grid-table cf-excel-list-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Agent Name</th>
                <th>Seized Vehicles</th>
                <th>Total Payment</th>
                <th>Paid</th>
                <th>Unpaid</th>
                <th>Extra Paid / Advance</th>
                <th className="cf-action-th">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>{emptyMessage}</td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.agentId}>
                    <td>
                      {serialFor(
                        index,
                        pagination.page || page,
                        pagination.total || rows.length,
                        PAGE_LIMIT
                      )}
                    </td>
                    <td>{row.agentName || "—"}</td>
                    <td>{row.seizedVehicles || 0}</td>
                    <td className="cf-amount-td cf-amount-td--total">{money(row.totalPayable)}</td>
                    <td className="cf-amount-td">{money(row.paid)}</td>
                    <td className="cf-amount-td">{money(row.unpaid)}</td>
                    <td className="cf-amount-td">{money(row.extraPaid)}</td>
                    <td className="cf-action-td company-actions-td">
                      <button
                        type="button"
                        className="company-btn company-btn--view"
                        onClick={() => openDetail(row.agentId)}
                        disabled={detailLoading}
                      >
                        <FiEye aria-hidden />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <PaginationBar
        page={pagination.page || page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        loading={loading}
        onPageChange={setPage}
      />

      {detail ? (
        <div className="rab-modal" role="dialog" aria-modal="true">
          <div className="rab-modal__panel">
            <div className="rab-modal__head">
              <div>
                <h3>{detail.agent?.name}</h3>
                <p className="muted">
                  {detail.agent?.phone || "—"} · Seized {detail.totals?.seizedVehicles || 0} ·
                  Payable {money(detail.totals?.totalPayable)} · Paid {money(detail.totals?.paid)} ·
                  Unpaid {money(detail.totals?.unpaid)} · Extra Paid / Advance{" "}
                  {money(detail.totals?.extraPaid)}
                </p>
              </div>
              <button type="button" className="secondary-page-btn" onClick={() => setDetail(null)}>
                Close
              </button>
            </div>

            <div className="company-table-wrap table-scroll">
              <table className="users-table excel-grid-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Customer Name</th>
                    <th>Bank / NBFC</th>
                    <th>Loan</th>
                    <th>Trace date</th>
                    <th>Seized date</th>
                    <th>Field agent</th>
                    <th>Repo Payment</th>
                    <th>Other Payment</th>
                    <th>Total Payable</th>
                    <th>Paid</th>
                    <th>Unpaid</th>
                    <th>Extra applied</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.vehicles || []).length === 0 ? (
                    <tr>
                      <td colSpan={13}>No seized vehicles for this agent.</td>
                    </tr>
                  ) : (
                    detail.vehicles.map((vehicle) => (
                      <tr key={vehicle.confirmationId}>
                        <td>{vehicle.vehicleNumber || "—"}</td>
                        <td>{vehicle.customerName || "—"}</td>
                        <td>{vehicle.bankName || "—"}</td>
                        <td>{vehicle.loanAccountNumber || "—"}</td>
                        <td>{formatDate(vehicle.tracedAt)}</td>
                        <td>{formatDate(vehicle.seizedAt)}</td>
                        <td>{vehicle.agentName || detail.agent?.name || "—"}</td>
                        <td className="cf-amount-td">{money(vehicle.repoPayable)}</td>
                        <td className="cf-amount-td">{money(vehicle.otherPayable)}</td>
                        <td className="cf-amount-td cf-amount-td--total">
                          {money(vehicle.totalPayable)}
                        </td>
                        <td className="cf-amount-td">{money(vehicle.paid)}</td>
                        <td className="cf-amount-td">{money(vehicle.unpaid)}</td>
                        <td className="cf-amount-td">{money(vehicle.extraApplied)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {(detail.vehicles || []).map((vehicle) => (
              <section key={`hist-${vehicle.confirmationId}`} className="rab-history-block">
                <h4>
                  Payment history · {vehicle.vehicleNumber || "Vehicle"}
                </h4>
                <div className="company-table-wrap table-scroll">
                  <table className="users-table excel-grid-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Mode</th>
                        <th>Reference</th>
                        <th>By</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(vehicle.payments || []).length === 0 ? (
                        <tr>
                          <td colSpan={7}>No payments recorded for this vehicle.</td>
                        </tr>
                      ) : (
                        vehicle.payments.map((payment) => (
                          <tr key={payment._id}>
                            <td>{formatDate(payment.paymentDate)}</td>
                            <td>{paymentTypeLabel(payment.paymentType)}</td>
                            <td className="cf-amount-td">{money(payment.amount)}</td>
                            <td>{MODE_LABELS[payment.paymentMode] || payment.paymentMode}</td>
                            <td>{payment.reference || "—"}</td>
                            <td>{payment.createdByName || "—"}</td>
                            <td>{payment.remarks || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}

            {(detail.agentLevelPayments || []).length > 0 ? (
              <section className="rab-history-block">
                <h4>Extra Paid / Advance and agent-level payments</h4>
                <div className="company-table-wrap table-scroll">
                  <table className="users-table excel-grid-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Mode</th>
                        <th>Reference</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.agentLevelPayments.map((payment) => (
                        <tr key={payment._id}>
                          <td>{formatDate(payment.paymentDate)}</td>
                          <td>{paymentTypeLabel(payment.paymentType)}</td>
                          <td className="cf-amount-td">{money(payment.amount)}</td>
                          <td>{MODE_LABELS[payment.paymentMode] || payment.paymentMode}</td>
                          <td>{payment.reference || "—"}</td>
                          <td>{payment.remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
